<script setup lang="ts">
type ButtonComponent = {
  $el?: HTMLElement;
};

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    participantName: string;
    loading?: boolean;
    errorMessage?: string;
  }>(),
  {
    loading: false,
    errorMessage: "",
  },
);

const emit = defineEmits<{
  cancel: [];
  confirm: [];
  "update:modelValue": [value: boolean];
}>();

const cancelButton = ref<ButtonComponent | null>(null);
const confirmationRequested = ref(false);
const titleId = useId();
const descriptionId = useId();
const errorId = useId();

const isBusy = computed(() => props.loading || confirmationRequested.value);
const describedBy = computed(() =>
  props.errorMessage ? `${descriptionId} ${errorId}` : descriptionId,
);

const focusCancelButton = async (): Promise<void> => {
  await nextTick();
  cancelButton.value?.$el?.focus();
};

const cancel = (): void => {
  if (isBusy.value) return;

  emit("cancel");
  emit("update:modelValue", false);
};

const confirm = (): void => {
  if (isBusy.value) return;

  confirmationRequested.value = true;
  emit("confirm");
};

const updateModelValue = (value: boolean): void => {
  if (value || isBusy.value) return;
  cancel();
};

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      await focusCancelButton();
      return;
    }

    confirmationRequested.value = false;
  },
);

watch(
  [() => props.loading, () => props.errorMessage],
  ([loading, errorMessage], [wasLoading, previousErrorMessage]) => {
    if (
      (wasLoading && !loading) ||
      (Boolean(errorMessage) && errorMessage !== previousErrorMessage)
    ) {
      confirmationRequested.value = false;
    }
  },
);
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    :persistent="isBusy"
    :z-index="2600"
    width="calc(100% - 32px)"
    max-width="440"
    :aria-labelledby="titleId"
    :aria-describedby="describedBy"
    :aria-busy="isBusy"
    data-testid="video-call-participant-removal-dialog"
    @update:model-value="updateModelValue"
  >
    <v-card rounded="xl" :aria-busy="isBusy">
      <v-card-title :id="titleId" class="removal-dialog__title">
        Remover participante?
      </v-card-title>

      <v-card-text class="removal-dialog__content">
        <p :id="descriptionId" class="text-body-1 mb-0">
          Remover {{ props.participantName }} da chamada? A pessoa será
          desconectada imediatamente e precisará solicitar entrada novamente
          para voltar.
        </p>

        <v-alert
          v-if="props.errorMessage"
          :id="errorId"
          class="mt-4"
          type="error"
          variant="tonal"
          density="compact"
          role="alert"
          aria-live="assertive"
          data-testid="video-call-participant-removal-error"
        >
          {{ props.errorMessage }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="removal-dialog__actions">
        <v-btn
          ref="cancelButton"
          type="button"
          variant="text"
          :disabled="isBusy"
          :aria-label="`Cancelar remoção de ${props.participantName}`"
          autofocus
          @click="cancel"
        >
          Cancelar
        </v-btn>
        <v-btn
          type="button"
          color="error"
          variant="flat"
          :disabled="isBusy"
          :aria-busy="isBusy"
          :aria-describedby="descriptionId"
          :aria-label="
            isBusy
              ? `Removendo ${props.participantName} da chamada`
              : `Remover ${props.participantName} da chamada`
          "
          data-testid="video-call-participant-removal-confirm"
          @click="confirm"
        >
          <v-progress-circular
            v-if="isBusy"
            class="mr-2"
            indeterminate
            :size="18"
            :width="2"
            aria-hidden="true"
          />
          {{ isBusy ? "Removendo…" : "Remover da chamada" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.removal-dialog__title {
  padding: 24px 24px 8px;
  color: #17233c;
  font-size: 1.15rem;
  font-weight: 700;
  white-space: normal;
}

.removal-dialog__content {
  padding: 8px 24px 24px;
  color: #526078;
  line-height: 1.5;
}

.removal-dialog__actions {
  justify-content: flex-end;
  gap: 8px;
  padding: 0 24px 24px;
}

@media (max-width: 479px) {
  .removal-dialog__actions {
    flex-direction: column-reverse;
    align-items: stretch;

    :deep(.v-btn) {
      width: 100%;
    }
  }
}
</style>
